import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Checkbox,
  Button,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
} from "@material-ui/core";
import Alert from "../Alert";
import useStyles from "./styles";
import {
  filtrosExibicao,
  filtrosOrganizacao,
  filtrosTipoApresentacao,
  PREFERENCIAS_APRESENTACAO,
  PREFERENCIAS_EXIBICAO,
  PREFERENCIAS_ORGANIZACAO,
} from "../../helper/resources/Constants";
import { errorDefault } from "../../helper/errorDefault";
import api from "../../config/api";
import PropTypes from "prop-types";
import { debitsFilterPreferences } from "../../actions";

function ModalPreferences({ openModal, setOpenModal }) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [filtroExibicao, setFiltroExibicao] = useState("");
  const [filtroOrganizacao, setFiltroOrganizacao] = useState("");
  const [filtroTipoApresentacao, setFiltroTipoApresentacao] = useState("");
  const [exibirPagos, setExibirPagos] = useState(false);
  const [exibirMensagemInicial, setExibirMensagemInicial] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [alertOptions, setAlertOptions] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const mapDbTitleToOption = useCallback((obj, value) => {
    return Object.keys(obj).find((key) => obj[key] === value);
  }, []);

  const getPreferencias = useCallback(async () => {
    try {
      const preferenciasResponse = await api.get("Usuarios/ObterPreferencias");
      if (preferenciasResponse.data !== null) {
        const retorno = preferenciasResponse?.data?.Retorno;

        dispatch(
          debitsFilterPreferences({
            filtroExibicao: mapDbTitleToOption(
              PREFERENCIAS_EXIBICAO,
              retorno?.CampoExibicaoTitulos
            ),
            filtroOrganizacao: mapDbTitleToOption(
              PREFERENCIAS_ORGANIZACAO,
              retorno?.CampoOrganizacaoTitulos
            ),
            filtroTipoApresentacao: mapDbTitleToOption(
              PREFERENCIAS_APRESENTACAO,
              retorno?.FormaExibicaoDebitos
            ),
            exibirPagos: retorno?.ExibirTitulosPagos,
            exibirMensagemInicial: retorno?.ExibirMensagemInicial,
          })
        );

        setFiltroExibicao(
          mapDbTitleToOption(
            PREFERENCIAS_EXIBICAO,
            retorno?.CampoExibicaoTitulos
          )
        );
        setFiltroOrganizacao(
          mapDbTitleToOption(
            PREFERENCIAS_ORGANIZACAO,
            retorno?.CampoOrganizacaoTitulos
          )
        );
        setFiltroTipoApresentacao(
          mapDbTitleToOption(
            PREFERENCIAS_APRESENTACAO,
            retorno?.FormaExibicaoDebitos
          )
        );
        setExibirPagos(retorno?.ExibirTitulosPagos);
        setExibirMensagemInicial(retorno?.ExibirMensagemInicial);
      }
    } catch (error) {
      setAlertOptions({
        open: true,
        message: errorDefault(error),
        severity: "error",
      });
    }
  }, [dispatch, mapDbTitleToOption]);

  useEffect(() => {
    getPreferencias();
  }, [getPreferencias]);

  async function salvarPreferencias() {
    setLoadingSave(true);
    try {
      const preferenciasResponse = await api.put(
        "Usuarios/AtualizarPreferencias",
        {
          CampoOrganizacaoTitulos: PREFERENCIAS_ORGANIZACAO[filtroOrganizacao],
          CampoExibicaoTitulos: PREFERENCIAS_EXIBICAO[filtroExibicao],
          FormaExibicaoDebitos:
            PREFERENCIAS_APRESENTACAO[filtroTipoApresentacao],
          ExibirTitulosPagos: exibirPagos,
          ExibirMensagemInicial: exibirMensagemInicial
        }
      );
      if (preferenciasResponse?.data?.Retorno) {
        setAlertOptions({
          open: true,
          message: "Preferências salvas com sucesso!",
          severity: "success",
        });

        dispatch(
          debitsFilterPreferences({
            filtroExibicao: filtroExibicao,
            filtroOrganizacao: filtroOrganizacao,
            filtroTipoApresentacao: filtroTipoApresentacao,
            exibirPagos,
            exibirMensagemInicial
          })
        );
      }
    } catch (error) {
      setAlertOptions({
        open: true,
        message: errorDefault(error),
        severity: "error",
      });
    } finally {
      setLoadingSave(false);
    }
  }

  return (
    <Dialog
      open={openModal}
      fullWidth={true}
      maxWidth={"sm"}
      onClose={() => setOpenModal(false)}
    >
      <DialogTitle>Preferências</DialogTitle>
      {alertOptions?.open && (
        <Alert
          open={alertOptions?.open}
          Close={() =>
            setAlertOptions({ open: false, message: "", severity: "" })
          }
          message={alertOptions?.message}
          severity={alertOptions?.severity}
        />
      )}
      <DialogContent dividers>
        <div className={classes.dialogContent}>
          <div className={classes.dropdownContent}>
            <InputLabel variant="standard" htmlFor="select-organizacao">
              Organizar por:
            </InputLabel>
            <Select
              value={filtroOrganizacao}
              className={classes.selectPreference}
              inputProps={{
                id: "select-organizacao",
                "data-testid": "select-organizacao",
              }}
              onChange={({ target }) => setFiltroOrganizacao(target?.value)}
            >
              <MenuItem value={filtrosOrganizacao.DATA_VENCIMENTO}>
                Data de Vencimento
              </MenuItem>
              <MenuItem value={filtrosOrganizacao.TIPO_DEBITO}>
                Tipo de Débito
              </MenuItem>
              <MenuItem value={filtrosOrganizacao.VALOR}>Valor</MenuItem>
            </Select>
          </div>
          <div className={classes.dropdownContent}>
            <InputLabel variant="standard" htmlFor="select-exibicao">
              Exibir:
            </InputLabel>
            <Select
              value={filtroExibicao}
              className={classes.selectPreference}
              inputProps={{
                id: "select-exibicao",
                "data-testid": "select-exibicao",
              }}
              onChange={({ target }) => setFiltroExibicao(target?.value)}
            >
              <MenuItem value={filtrosExibicao.TODOS}>Todos</MenuItem>
              <MenuItem value={filtrosExibicao.PAGOS}>Pagos</MenuItem>
              <MenuItem value={filtrosExibicao.VENCIDOS}>Vencidos</MenuItem>
              <MenuItem value={filtrosExibicao.VINCENDOS}>Vincendos</MenuItem>
            </Select>
          </div>
        </div>
        <br />
        <div className={classes.dropdownContent}>
          <InputLabel variant="standard" htmlFor="select-organizacao">
            Tipo de Apresentação de Débitos:
          </InputLabel>
          <Select
            value={filtroTipoApresentacao}
            className={classes.selectPreference}
            inputProps={{
              id: "select-organizacao",
              "data-testid": "select-organizacao",
            }}
            onChange={({ target }) => setFiltroTipoApresentacao(target?.value)}
          >
            <MenuItem value={filtrosTipoApresentacao.CARD}>Cards</MenuItem>
            <MenuItem value={filtrosTipoApresentacao.LIST}>Lista</MenuItem>
          </Select>
        </div>
        <div style={{ marginTop: 20, display: "flex" }}>
          <div>
            Exibir Títulos Pagos:
            <Checkbox
              className={classes.checkBoxPreference}
              checked={exibirPagos}
              onChange={({ target }) => setExibirPagos(target?.checked)}
            />
          </div>
          <div style={{ marginLeft: 5 }}>
            Exibir Mensagem Inicial:
            <Checkbox
              className={classes.checkBoxPreference}
              checked={exibirMensagemInicial}
              onChange={({ target }) => setExibirMensagemInicial(target?.checked)}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={() => setOpenModal(false)}>
          Fechar
        </Button>
        <Button
          onClick={() => salvarPreferencias()}
          endIcon={
            loadingSave && <CircularProgress color={"inherit"} size={15} />
          }
          disabled={loadingSave}
          color="primary"
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ModalPreferences.propTypes = {
  openModal: PropTypes.bool.isRequired,
  setOpenModal: PropTypes.func.isRequired,
};

export default ModalPreferences;
